import React, { useState } from "react";
import RealtimeAudioCapture from "../components/translation/RealtimeAudioCapture";
import AudioUploader from "../components/translation/AudioUploader";
import TranscriptionDisplay from "../components/translation/TranscriptionDisplay";
import TranslationDisplay from "../components/translation/TranslationDisplay";
import SummarizationDisplay from "../components/translation/SummarizationDisplay";
import LearningMode from "../components/translation/LearningMode";
import LanguageSelector from "../components/common/LanguageSelector";
import { translateText } from "../services/api";
import { LanguageOption } from "../types";

const TranslationPage: React.FC = () => {
  const [transcription, setTranscription] = useState<string>("");
  const [translation, setTranslation] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isRealtimeRecording, setIsRealtimeRecording] =
    useState<boolean>(false);
  const [targetLanguage, setTargetLanguage] = useState<string>("en");
  const [sourceLanguage, setSourceLanguage] = useState<string>("zh");
  const [context, setContext] = useState<string>("");
  const [autoGeneratedContext, setAutoGeneratedContext] = useState<string>("");
  const [summary, setSummary] = useState<string>("");
  // Default to dashscope (Qwen-MT) - hidden from UI
  const translationProvider = "dashscope";

  // Auto-generate context from transcription when user hasn't provided one
  React.useEffect(() => {
    if (!context.trim() && transcription.trim().length > 50) {
      // Extract key information from transcription to create context
      // Simple approach: extract first few sentences and key terms
      const sentences = transcription.split(/[。！？.!?]/).filter(s => s.trim().length > 0);
      const firstFewSentences = sentences.slice(0, 3).join('。').trim();
      
      // Try to identify topic/subject from transcription
      let generatedContext = '';
      
      // Check for common course/lecture indicators
      if (transcription.includes('课程') || transcription.includes('课') || transcription.includes('讲座')) {
        generatedContext = `${sourceLanguage === 'zh' ? 'Chinese' : sourceLanguage === 'vi' ? 'Vietnamese' : 'English'} course lecture.`;
      } else if (transcription.includes('会议') || transcription.includes('meeting')) {
        generatedContext = 'Business meeting or discussion.';
      } else if (transcription.includes('医疗') || transcription.includes('medical')) {
        generatedContext = 'Medical consultation or discussion.';
      } else {
        // Generic context based on first sentences
        generatedContext = `${sourceLanguage === 'zh' ? 'Chinese' : sourceLanguage === 'vi' ? 'Vietnamese' : 'English'} conversation or lecture.`;
      }
      
      // Add topic if we can extract it from first sentences
      if (firstFewSentences.length > 0 && firstFewSentences.length < 100) {
        generatedContext += ` Topic: ${firstFewSentences}`;
      }
      
      setAutoGeneratedContext(generatedContext);
    } else if (!context.trim()) {
      setAutoGeneratedContext('');
    }
  }, [transcription, context, sourceLanguage]);

  const handleManualTranslate = async () => {
    if (!transcription || !transcription.trim()) {
      return;
    }

    setIsProcessing(true);
    try {
      const result = await translateText(
        transcription,
        sourceLanguage,
        targetLanguage,
        null, // model - use default
        translationProvider, // provider
        (context.trim() || autoGeneratedContext) || null // context
      );
      setTranslation(result.translated_text);
    } catch (error) {
      console.error("Translation error:", error);
      setTranslation("Translation failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const sourceLanguageOptions: LanguageOption[] = [
    { value: "zh", label: "Chinese (中文)" },
    { value: "en", label: "English" },
    { value: "vi", label: "Vietnamese" },
  ];

  const targetLanguageOptions: LanguageOption[] = [
    { value: "en", label: "English" },
    { value: "vi", label: "Vietnamese" },
    { value: "zh", label: "Chinese (中文)" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 text-center shadow-md">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">
          Realtime Translation
        </h1>
        {/* <p className="text-sm md:text-base opacity-90">
          Translate Chinese courses in real-time
        </p> */}
      </header>

      <div className="flex-1 max-w-7xl mx-auto px-4 md:px-8 py-6 w-full">
        <div className="flex flex-col gap-6">
          {/* Language Selectors and Recording Controls */}
          <div className="bg-white rounded-lg p-4 md:p-5 shadow-md">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center">
                {/* Language Selectors */}
                <div className="flex gap-4 flex-1 flex-wrap">
                  <LanguageSelector
                    label="Source Language"
                    value={sourceLanguage}
                    onChange={setSourceLanguage}
                    options={sourceLanguageOptions}
                  />
                  <LanguageSelector
                    label="Target Language"
                    value={targetLanguage}
                    onChange={setTargetLanguage}
                    options={targetLanguageOptions}
                  />
                </div>

                {/* Divider for desktop */}
                <div className="hidden md:block w-px h-12 bg-gray-200"></div>

                {/* Recording Controls */}
                <div className="w-full md:w-auto md:flex-shrink-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <RealtimeAudioCapture
                        onTranscriptionUpdate={(text) => {
                          setTranscription((prev) => {
                            // Append new transcription
                            return prev ? `${prev} ${text}` : text;
                          });
                        }}
                        onTranslationUpdate={(text) => {
                          setTranslation((prev) => {
                            // Append new translation
                            return prev ? `${prev} ${text}` : text;
                          });
                        }}
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        context={context.trim() || autoGeneratedContext}
                        translationProvider={translationProvider}
                        isRecording={isRealtimeRecording}
                        onRecordingChange={setIsRealtimeRecording}
                      />
                    </div>
                    {/* Clear Button */}
                    {(transcription || translation || summary) && (
                      <button
                        onClick={() => {
                          if (isRealtimeRecording) {
                            // Stop recording first if active
                            setIsRealtimeRecording(false);
                          }
                          setTranscription("");
                          setTranslation("");
                          setSummary("");
                          setAutoGeneratedContext("");
                        }}
                        disabled={isRealtimeRecording}
                        className="px-3 py-2 rounded-lg font-semibold text-white bg-gray-500 hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 self-start"
                        title={isRealtimeRecording ? "Stop recording first to clear" : "Clear all transcription and translation"}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span className="hidden sm:inline">Clear</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Audio Upload Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700">Or Upload Audio File</h3>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                  <AudioUploader
                    onTranscriptionComplete={(text) => {
                      setTranscription(text);
                    }}
                    onTranslationComplete={(text) => {
                      setTranslation(text);
                    }}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    context={context.trim() || autoGeneratedContext}
                    translationProvider={translationProvider}
                  />
                </div>
              </div>

              {/* Context Input */}
              <div className="border-t border-gray-200 pt-4">
                <label
                  htmlFor="context"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Context (Optional)
                </label>
                <textarea
                  id="context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="e.g., Chinese CS course lecture&#10;Business meeting&#10;Medical consultation"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                />
                <div className="mt-2 flex flex-col gap-2">
                  {autoGeneratedContext && !context.trim() && (
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
                      <strong>Auto-generated context:</strong> {autoGeneratedContext}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Enter context to improve translation accuracy. Context will be automatically generated from the conversation content if you don't fill it in.
                    </p>
                    <button
                      onClick={() => {
                        // Trigger context update in RealtimeAudioCapture
                        // Note: ASR context can only be set at recording start,
                        // but translation context can be updated anytime
                        const event = new CustomEvent("updateContext", {
                          detail: context.trim() || autoGeneratedContext,
                        });
                        window.dispatchEvent(event);
                      }}
                      disabled={!isRealtimeRecording}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-xs font-semibold transition-colors hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        !isRealtimeRecording
                          ? "Start recording to update context"
                          : "Update translation context (ASR context requires restart)"
                      }
                    >
                      Update Context
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Transcription and Translation Displays */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TranscriptionDisplay
              text={transcription}
              language={sourceLanguage}
            />
            <TranslationDisplay
              text={translation}
              isLoading={isProcessing}
              onRetranslate={handleManualTranslate}
              hasTranscription={!!transcription}
              targetLang={targetLanguage}
            />
          </div>

          {/* Summarization Display */}
          <SummarizationDisplay
            conversationHistory={(() => {
              // Format conversation history as a readable text
              // Combine transcription and translation
              const parts: string[] = [];
              if (transcription && transcription.trim()) {
                parts.push(
                  `[${sourceLanguage.toUpperCase()}] ${transcription}`
                );
              }
              if (translation && translation.trim()) {
                parts.push(`[${targetLanguage.toUpperCase()}] ${translation}`);
              }
              return parts.join("\n\n");
            })()}
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            context={context.trim() || autoGeneratedContext}
            translationProvider={translationProvider}
            onSummaryGenerated={setSummary}
          />

          {/* Learning Mode */}
          {summary && summary.trim() && (
            <LearningMode
              conversationSummary={summary}
              conversationHistory={(() => {
                // Format conversation history for learning mode
                const parts: string[] = [];
                if (transcription && transcription.trim()) {
                  parts.push(
                    `[${sourceLanguage.toUpperCase()}] ${transcription}`
                  );
                }
                if (translation && translation.trim()) {
                  parts.push(
                    `[${targetLanguage.toUpperCase()}] ${translation}`
                  );
                }
                return parts.join("\n\n");
              })()}
              sourceLanguage={sourceLanguage}
              targetLanguage={targetLanguage}
              context={context.trim() || autoGeneratedContext}
              translationProvider={translationProvider}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationPage;
